import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import { Table, Checkbox, message, Card, Row, Col, Input } from "antd";
import { API_URL } from "../../url_config";
import {NavigationStepsKeolisNoc} from "../../components/navbars/navbar_keolis_noc"
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";
const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TablePriseService({ record, onChange }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handlePatch = (field, value) => {
    onChange(record.key, field, value);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handlePatch("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handlePatch("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationPriseServiceKeolis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pendingUpdatesRef = useRef(new Map());
  const timeoutRefs = useRef(new Map());
  const isMountedRef = useRef(true);

  // Fonction pour charger les données
  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(
        `${API_URL}/validations/validation_prise_service/?ot_tag=keolis_noc`
      );
      if (!isMountedRef.current) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          coquille: item.coquille,
          ligne_associee_coquille: item.ligne_associee_coquille,
          presence_coquille_sur_ihm_tr: item.presence_coquille_sur_ihm_tr,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
  }, []);

  // Charger les données au montage uniquement
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  // Fonction pour envoyer la mise à jour au serveur
  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;

    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_prise_service/${update.id}/`,
        { [update.field]: update.value }
      );

      const currentUpdate = pendingUpdatesRef.current.get(updateKey);
      if (currentUpdate && currentUpdate.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  };

  // ✅ Mise à jour locale + serveur avec debounce
  const handleDataChange = (key, field, value) => {
    let itemId;

    setData((prev) =>
      prev.map((item) => {
        if (item.key === key) {
          itemId = item.id;
          return { ...item, [field]: value };
        }
        return item;
      })
    );

    if (itemId) {
      const updateKey = `${itemId}_${field}`;
      
      const existingTimeout = timeoutRefs.current.get(updateKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      pendingUpdatesRef.current.set(updateKey, {
        id: itemId,
        field,
        value,
        timestamp: Date.now()
      });

      const timeoutId = setTimeout(() => {
        sendUpdate(updateKey);
        timeoutRefs.current.delete(updateKey);
      }, 1000);

      timeoutRefs.current.set(updateKey, timeoutId);
    }
  };

  // Filtrer les données selon la recherche
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.coquille?.toString().toLowerCase() || "").includes(search) ||
      (item.ligne_associee_coquille?.toString().toLowerCase() || "").includes(search) ||
      (item.comment?.toString().toLowerCase() || "").includes(search) ||
      (item.aide?.toString().toLowerCase() || "").includes(search)
    );
  });

  const columns = [
    {
      title: "Coquille",
      dataIndex: "coquille",
      key: "coquille",
      sorter: (a, b) => (a.coquille || "").localeCompare(b.coquille || ""),
    },
    {
      title: "Ligne associée à la coquille",
      dataIndex: "ligne_associee_coquille",
      key: "ligne_associee_coquille",
      sorter: (a, b) => (a.ligne_associee_coquille || "").localeCompare(b.ligne_associee_coquille || ""),
    },
    {
      title: "Présence sur IHM TR",
      dataIndex: "presence_coquille_sur_ihm_tr",
      key: "presence_coquille_sur_ihm_tr",
      sorter: (a, b) => (a.presence_coquille_sur_ihm_tr === b.presence_coquille_sur_ihm_tr ? 0 : a.presence_coquille_sur_ihm_tr ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_coquille_sur_ihm_tr", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
    title: "ETAT",
    dataIndex: "signal",
    sorter: (a, b) => a.signal - b.signal,
    render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
    },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsKeolisNoc selectedKey="prise_service" />
            <CoquillesTable url={`${API_URL}/validations/counter/prise_service/?ot_tag=keolis_noc`} />
      <Input
        placeholder="Rechercher dans toutes les colonnes..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ 
          marginBottom: "16px", 
          width: "100%",
          maxWidth: "500px"
        }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TablePriseService
              record={record}
              onChange={handleDataChange}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />

      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_prise_service/`}
              ot_tag="keolis_noc"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "Coquille", dataIndex: "coquille", key: "coquille" },
                { title: "Ligne associée", dataIndex: "ligne_associee_coquille", key: "ligne_associee_coquille" },
              ]}
              formFields={[
                { name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
                { name: "ligne_associee_coquille", label: "Ligne associée", placeholder: "Entrez la ligne associée" },
              ]}
              onRefresh={fetchData}
            />
      </div>


    </div>
  );
};

export default ValidationPriseServiceKeolis;
